import { RouterLink } from '@angular/router';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { ShamArrowDown01StrokeIcon, ShamLogout01BulkIcon, ShamSettingsIcon } from 'sham-components/icons';

@Component({
  selector: 'sham-dashboard-account-menu',
  imports: [RouterLink, ShamArrowDown01StrokeIcon, ShamLogout01BulkIcon, ShamSettingsIcon],
  host: { '(document:click)': 'onDocumentClick($event)' },
  template: `
    <div class="account-menu">
      <button type="button" class="account-trigger" aria-haspopup="menu" [attr.aria-expanded]="open()" (click)="toggle()">
        <span class="account-initials" aria-hidden="true">{{ initials() }}</span>
        <span class="account-name">{{ name() }}</span>
        <sham-i-arrow-down-01-Stroke class="account-chevron" [class.account-chevron-open]="open()" />
      </button>
      @if (open()) {
        <div class="account-dropdown" role="menu">
          <a class="account-action" role="menuitem" [routerLink]="settingsPath()" (click)="close()">
            <sham-i-Settings />
            <span>Account Settings</span>
          </a>
          <button type="button" class="account-action" role="menuitem" (click)="onLogout()">
            <sham-i-logout-01-Bulk />
            <span>Logout</span>
          </button>
        </div>
      }
    </div>
  `,
  styles: `
    :host { display: block; }
    .account-menu { position: relative; }
    .account-trigger { display: flex; align-items: center; gap: 0.75rem; border: 0; background: transparent; color: #191c1f; font: inherit; cursor: pointer; }
    .account-initials { display: flex; width: 2rem; height: 2rem; align-items: center; justify-content: center; border-radius: 9999px; background: #eaf4fa; color: #1f6f8b; font-size: 0.875rem; font-weight: 600; }
    .account-name { font-size: 1rem; font-weight: 500; }
    .account-chevron { width: 1.25rem; height: 1.25rem; transition: transform 150ms ease; }
    .account-chevron-open { transform: rotate(180deg); }
    .account-dropdown { position: absolute; z-index: 100; top: calc(100% + 0.5rem); right: 0; width: 13rem; overflow: hidden; border: 1px solid #e5e7eb; border-radius: 0.5rem; background: #ffffff; box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1); }
    .account-action { display: flex; width: 100%; align-items: center; gap: 0.75rem; border: 0; padding: 0.75rem 1rem; color: #191c1f; background: transparent; font: inherit; font-size: 0.875rem; text-align: start; text-decoration: none; cursor: pointer; }
    .account-action:hover, .account-action:focus-visible { color: #2192ba; background: #eaf4fa; outline: none; }
    @media (max-width: 639px) { .account-name { display: none; } .account-trigger { gap: 0.5rem; } }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardAccountMenu {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly name = input.required<string>();
  readonly settingsPath = input.required<readonly string[]>();
  readonly logout = output<void>();
  readonly open = signal(false);
  readonly initials = computed(() => this.name().split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase() ?? '').join(''));

  toggle() { this.open.update((value) => !value); }
  close() { this.open.set(false); }
  onLogout() { this.close(); this.logout.emit(); }
  onDocumentClick(event: MouseEvent) { if (!this.host.nativeElement.contains(event.target as Node)) this.close(); }
}
